import type { KeyboardEvent, RefObject } from 'react';
import type { LiksDropdownOption } from './LiksDropdown.types';
import { TYPEAHEAD_RESET_MS } from './LiksDropdown.types';

export type LiksDropdownTypeaheadState = {
  query: string;
  timeoutId: number;
};

export function findDropdownTypeaheadIndex(
  visibleOptions: LiksDropdownOption[],
  query: string,
  fromIndex: number,
): number {
  const normalizedQuery = query.toLowerCase();
  if (normalizedQuery === '') {
    return -1;
  }

  const searchOrder = [
    ...visibleOptions.slice(fromIndex),
    ...visibleOptions.slice(0, fromIndex),
  ];

  const startsWithMatch = searchOrder.find((option) =>
    option.label.toLowerCase().startsWith(normalizedQuery),
  );
  if (startsWithMatch) {
    return visibleOptions.indexOf(startsWithMatch);
  }

  const includesMatch = searchOrder.find((option) =>
    option.label.toLowerCase().includes(normalizedQuery),
  );
  return includesMatch ? visibleOptions.indexOf(includesMatch) : -1;
}

export function handleDropdownTypeaheadKey(options: {
  key: string;
  filterable: boolean;
  isOpen: boolean;
  activeIndex: number;
  selectedIndex: number;
  optionsLength: number;
  visibleOptions: LiksDropdownOption[];
  typeaheadRef: RefObject<LiksDropdownTypeaheadState>;
  setActiveIndex: (index: number) => void;
  selectIndex: (index: number) => void;
}): boolean {
  const {
    key,
    filterable,
    isOpen,
    activeIndex,
    selectedIndex,
    optionsLength,
    visibleOptions,
    typeaheadRef,
    setActiveIndex,
    selectIndex,
  } = options;

  if (filterable || key.length !== 1 || key === ' ') {
    return false;
  }

  window.clearTimeout(typeaheadRef.current.timeoutId);
  const nextQuery = `${typeaheadRef.current.query}${key}`;
  typeaheadRef.current.query = nextQuery;
  typeaheadRef.current.timeoutId = window.setTimeout(() => {
    typeaheadRef.current.query = '';
  }, TYPEAHEAD_RESET_MS);

  const currentIndex = isOpen
    ? activeIndex
    : selectedIndex >= 0
      ? selectedIndex
      : 0;
  const fromIndex =
    nextQuery.length === 1 ? (currentIndex + 1) % optionsLength : 0;
  const matchIndex = findDropdownTypeaheadIndex(
    visibleOptions,
    nextQuery,
    fromIndex,
  );
  if (matchIndex < 0) {
    return true;
  }

  if (isOpen) {
    setActiveIndex(matchIndex);
  } else {
    selectIndex(matchIndex);
  }

  return true;
}

export function handleDropdownListNavigationKeyDown(options: {
  event: KeyboardEvent<HTMLElement>;
  isOpen: boolean;
  activeIndex: number;
  visibleOptionsLength: number;
  open: () => void;
  close: () => void;
  moveActiveIndex: (nextIndex: number) => void;
  selectIndex: (index: number) => void;
  focusTrigger: () => void;
}): boolean {
  const {
    event,
    isOpen,
    activeIndex,
    visibleOptionsLength,
    open,
    close,
    moveActiveIndex,
    selectIndex,
    focusTrigger,
  } = options;

  switch (event.key) {
    case 'ArrowDown': {
      event.preventDefault();
      if (!isOpen) {
        open();
        break;
      }
      moveActiveIndex(activeIndex + 1);
      break;
    }
    case 'ArrowUp': {
      event.preventDefault();
      if (!isOpen) {
        open();
        break;
      }
      moveActiveIndex(activeIndex - 1);
      break;
    }
    case 'Home': {
      if (!isOpen) {
        return false;
      }
      event.preventDefault();
      moveActiveIndex(0);
      break;
    }
    case 'End': {
      if (!isOpen) {
        return false;
      }
      event.preventDefault();
      moveActiveIndex(visibleOptionsLength - 1);
      break;
    }
    case 'Enter': {
      if (!isOpen) {
        return false;
      }
      event.preventDefault();
      selectIndex(activeIndex);
      break;
    }
    case 'Escape': {
      if (!isOpen) {
        return false;
      }
      event.preventDefault();
      close();
      focusTrigger();
      break;
    }
    default:
      return false;
  }
  return true;
}
